import { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Clock, Send, CheckCircle, Loader2, MessageCircle } from 'lucide-react';
import emailjs from 'emailjs-com';
import { useScrollAnimation } from '../hooks/useScrollAnimation';
import { companyInfo } from '../data';

// EmailJS Configuration
const EMAILJS_SERVICE_ID = 'service_f4p3v1j';
const EMAILJS_TEMPLATE_ID = 'template_tsh2pwi';
const EMAILJS_PUBLIC_KEY = 'Yb3KC_5Ul2KrHkUTg';

// Blob Background Component
function BlobBackground() {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {/* Blob 1 - Top Left */}
      <motion.div
        animate={{
          x: [0, 100, 0],
          y: [0, 50, 0],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 15,
          repeat: Infinity,
          ease: "easeInOut",
        }}
        className="absolute -top-20 -left-20 w-[450px] h-[450px] bg-gradient-to-br from-blue-500/25 to-primary-600/20 rounded-full blur-[80px]"
      />
      
      {/* Blob 2 - Top Right */}
      <motion.div
        animate={{
          x: [0, -80, 0],
          y: [0, 100, 0],
          scale: [1, 1.4, 1],
        }}
        transition={{
          duration: 18,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 2,
        }}
        className="absolute -top-10 -right-20 w-[400px] h-[400px] bg-gradient-to-br from-cyan-500/20 to-blue-500/20 rounded-full blur-[75px]"
      />
      
      {/* Blob 3 - Bottom Left */}
      <motion.div
        animate={{
          x: [0, 60, 0],
          y: [0, -80, 0],
          scale: [1, 1.5, 1],
        }}
        transition={{
          duration: 16,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 4,
        }}
        className="absolute -bottom-10 -left-10 w-[420px] h-[420px] bg-gradient-to-tr from-primary-600/25 to-blue-500/20 rounded-full blur-[80px]"
      />
      
      {/* Blob 4 - Bottom Right */}
      <motion.div
        animate={{
          x: [0, -100, 0],
          y: [0, -60, 0],
          scale: [1, 1.3, 1],
        }}
        transition={{
          duration: 14,
          repeat: Infinity,
          ease: "easeInOut",
          delay: 3,
        }}
        className="absolute -bottom-20 right-0 w-[380px] h-[380px] bg-gradient-to-bl from-blue-500/20 to-primary-600/15 rounded-full blur-[70px]"
      />
    </div>
  );
}

const contactInfo = [
  {
    icon: MapPin,
    title: 'Alamat',
    content: companyInfo.address,
    href: `https://maps.google.com/?q=${encodeURIComponent(companyInfo.address)}`,
  },
  {
    icon: MessageCircle,
    title: 'WhatsApp',
    content: companyInfo.whatsapp,
    href: `https://wa.me/${companyInfo.whatsapp.replace(/\D/g, '')}`,
  },
  {
    icon: Clock,
    title: 'Jam Operasional',
    content: 'Senin - Jumat: 08:00 - 17:00 WIB',
    href: null,
  },
];

export function Contact() {
  const { ref, isInView } = useScrollAnimation({ threshold: 0.2 });
  const [formState, setFormState] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    message: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');
    
    try {
      const templateParams = {
        from_name: formState.name,
        email: formState.email,
        phone: formState.phone,
        service: formState.service,
        message: formState.message,
      };
      
      await emailjs.send(
        EMAILJS_SERVICE_ID,
        EMAILJS_TEMPLATE_ID,
        templateParams,
        EMAILJS_PUBLIC_KEY
      );
      
      setSubmitStatus('success');
      
      // Reset form after 3 seconds
      setTimeout(() => {
        setSubmitStatus('idle');
        setFormState({
          name: '',
          email: '',
          phone: '',
          service: '',
          message: '',
        });
      }, 3000);
    } catch (error) {
      console.error('EmailJS Error:', error);
      setSubmitStatus('error');
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    setFormState((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  return (
    <section
      id="contact"
      ref={ref}
      className="relative py-24 lg:py-32 overflow-hidden"
    >
      {/* Background */}
      <div className="absolute inset-0 bg-white" />

      {/* Blob Animation Background */}
      <BlobBackground />

      <div className="relative max-w-7xl mx-auto section-padding z-10">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          animate={isInView ? { opacity: 1, y: 0 } : {}}
          transition={{ duration: 0.8 }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={isInView ? { opacity: 1, scale: 1 } : {}}
            transition={{ duration: 0.5 }}
            className="inline-flex items-center gap-2 px-4 py-2 bg-primary-100 rounded-full mb-6"
          >
            <span className="w-2 h-2 bg-primary-600 rounded-full" />
            <span className="text-sm font-medium text-primary-700">Hubungi Kami</span>
          </motion.div>

          <h2 className="font-display text-3xl md:text-4xl lg:text-5xl font-bold text-dark-900 mb-6">
            Mari
            <span className="text-gradient"> Bekerja Sama</span>
          </h2>

          <p className="text-lg text-dark-600">
            Hubungi kami untuk konsultasi gratis dan diskusikan kebutuhan bisnis Anda
          </p>
        </motion.div>

        <div className="grid lg:grid-cols-5 gap-12">
          {/* Contact Info */}
          <motion.div
            initial={{ opacity: 0, x: -30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="lg:col-span-2 space-y-6"
          >
            <div className="glass-dark rounded-3xl p-8 shadow-lg bg-[#0a1628] border border-blue-900/30">
              <h3 className="text-2xl font-bold text-white mb-6">Informasi Kontak</h3>
              
              <div className="space-y-6">
                {contactInfo.map((item, index) => {
                  const Icon = item.icon;
                  const content = (
                    <>
                      <div className="flex-shrink-0">
                        <div className="w-12 h-12 bg-blue-900/50 rounded-xl flex items-center justify-center"
                        >
                          <Icon className="w-5 h-5 text-blue-300" />
                        </div>
                      </div>
                      <div>
                        <p className="text-sm text-blue-200 mb-1">{item.title}</p>
                        <p className="text-white font-medium">{item.content}</p>
                      </div>
                    </>
                  );

                  return item.href ? (
                    <motion.a
                      key={item.title}
                      href={item.href}
                      target={item.href.startsWith('http') ? '_blank' : undefined}
                      rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      whileHover={{ x: 5 }}
                      className="flex gap-4 group cursor-pointer"
                    >
                      {content}
                    </motion.a>
                  ) : (
                    <motion.div
                      key={item.title}
                      initial={{ opacity: 0, y: 20 }}
                      animate={isInView ? { opacity: 1, y: 0 } : {}}
                      transition={{ duration: 0.5, delay: 0.3 + index * 0.1 }}
                      className="flex gap-4"
                    >
                      {content}
                    </motion.div>
                  );
                })}
              </div>
            </div>

            {/* Map Placeholder */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={isInView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.8, delay: 0.5 }}
              className="glass-dark rounded-3xl overflow-hidden shadow-lg aspect-video lg:aspect-auto lg:h-64 bg-[#0a1628] border-[12px] border-[#0d1d33]"
            >
              <iframe
                src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3957.1355482926!2d112.7687!3d-7.3359!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zN8KwMjAnMDkuMiJTIDExMsKwNDYnMDcuMyJF!5e0!3m2!1sen!2sid!4v1600000000000!5m2!1sen!2sid"
                width="100%"
                height="100%"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="Lokasi Emporium Solution"
                className="grayscale-[20%] hover:grayscale-0 transition-all duration-500"
              />
            </motion.div>
          </motion.div>

          {/* Contact Form */}
          <motion.div
            initial={{ opacity: 0, x: 30 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="lg:col-span-3"
          >
            <div className="glass-dark rounded-3xl p-8 md:p-10 shadow-xl bg-[#0a1628] border border-blue-900/30">
              <h3 className="text-2xl font-bold text-white mb-6">Kirim Pesan</h3>
              
              {submitStatus === 'success' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-20 h-20 bg-green-900/30 rounded-full flex items-center justify-center mb-6"
                  >
                    <CheckCircle className="w-10 h-10 text-green-400" />
                  </motion.div>
                  <h4 className="text-2xl font-bold text-white mb-2">Pesan Terkirim!</h4>
                  <p className="text-blue-200">Tim kami akan segera menghubungi Anda.</p>
                </motion.div>
              ) : submitStatus === 'error' ? (
                <motion.div
                  initial={{ opacity: 0, scale: 0.9 }}
                  animate={{ opacity: 1, scale: 1 }}
                  className="flex flex-col items-center justify-center py-16 text-center"
                >
                  <motion.div
                    initial={{ scale: 0 }}
                    animate={{ scale: 1 }}
                    transition={{ type: 'spring', stiffness: 200, damping: 15 }}
                    className="w-20 h-20 bg-red-900/30 rounded-full flex items-center justify-center mb-6"
                  >
                    <span className="text-red-400 text-4xl">✕</span>
                  </motion.div>
                  <h4 className="text-2xl font-bold text-white mb-2">Gagal Mengirim</h4>
                  <p className="text-blue-200">Terjadi kesalahan. Silakan coba lagi.</p>
                </motion.div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="name" className="block text-sm font-medium text-blue-200 mb-2">
                        Nama Lengkap
                      </label>
                      <input
                        type="text"
                        id="name"
                        name="name"
                        required
                        value={formState.name}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-blue-800/50 bg-[#0d1d33] text-white placeholder-blue-400/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                        placeholder="Masukkan nama Anda"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="email" className="block text-sm font-medium text-blue-200 mb-2">
                        Email
                      </label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        required
                        value={formState.email}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-blue-800/50 bg-[#0d1d33] text-white placeholder-blue-400/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                        placeholder="email@anda.com"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-6">
                    <div>
                      <label htmlFor="phone" className="block text-sm font-medium text-blue-200 mb-2">
                        Nomor Telepon
                      </label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formState.phone}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-blue-800/50 bg-[#0d1d33] text-white placeholder-blue-400/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                        placeholder="0812-3456-7890"
                      />
                    </div>
                    
                    <div>
                      <label htmlFor="service" className="block text-sm font-medium text-blue-200 mb-2">
                        Layanan yang Diminati
                      </label>
                      <select
                        id="service"
                        name="service"
                        value={formState.service}
                        onChange={handleChange}
                        className="w-full px-4 py-3 rounded-xl border border-blue-800/50 bg-[#0d1d33] text-white focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all"
                      >
                        <option value="" className="bg-[#0d1d33]">Pilih layanan</option>
                        <option value="business-plan" className="bg-[#0d1d33]">Business Plan & Feasibility Study</option>
                        <option value="market-research" className="bg-[#0d1d33]">Market & Marketing Research</option>
                        <option value="finance" className="bg-[#0d1d33]">Finance, Tax & Accounting</option>
                        <option value="proposal" className="bg-[#0d1d33]">Business Proposal & Pitch Deck</option>
                        <option value="consultant" className="bg-[#0d1d33]">Business Consultant</option>
                        <option value="it-service" className="bg-[#0d1d33]">ERP Solutions & Business Automation</option>
                        <option value="strategy" className="bg-[#0d1d33]">Business Strategy Development</option>
                        <option value="hr" className="bg-[#0d1d33]">Human Resource Planning & Training</option>
                        <option value="analysis" className="bg-[#0d1d33]">Industry & Economic Analysis</option>
                        <option value="other" className="bg-[#0d1d33]">Lainnya (Other)</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-blue-200 mb-2">
                      Pesan
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      rows={4}
                      required
                      value={formState.message}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border border-blue-800/50 bg-[#0d1d33] text-white placeholder-blue-400/50 focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 outline-none transition-all resize-none"
                      placeholder="Ceritakan tentang kebutuhan bisnis Anda..."
                    />
                  </div>
                  
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                    className="w-full bg-blue-500 hover:bg-blue-400 text-white font-semibold py-4 rounded-xl flex items-center justify-center gap-2 disabled:opacity-70 transition-colors"
                  >
                    {isSubmitting ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Mengirim...
                      </>
                    ) : (
                      <>
                        <Send className="w-5 h-5" />
                        Kirim Pesan
                      </>
                    )}
                  </motion.button>
                </form>
              )}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}